import { INewbuilding } from '../types/newbuilding';
import { IMarkedListItem, IOffersDataState } from '../types/offersData';

/**
 * Санитайзер HTML. Реализации зависят от окружения:
 * на ноде — `sanitizeHtmlNode` (sanitize-html, без DOM), в браузере — `sanitizeHtmlBrowser` (DOMPurify).
 */
export type TSanitizeHtml = (html: string) => string;

const H3_REGEXP = /<h3.*?<\/h3>/gm;

function sanitizeNewbuilding(newbuilding: INewbuilding, sanitizeHtml: TSanitizeHtml): INewbuilding {
  if (!newbuilding.description) {
    return newbuilding;
  }

  return { ...newbuilding, description: sanitizeHtml(newbuilding.description.replace(H3_REGEXP, '')) };
}

function sanitizeMarkedListItem(item: IMarkedListItem, sanitizeHtml: TSanitizeHtml): IMarkedListItem {
  return {
    ...item,
    title: sanitizeHtml(item.title),
    content: sanitizeHtml(item.content),
  };
}

/**
 * Единая точка санитизации HTML, который выдача рендерит через dangerouslySetInnerHTML:
 * описания ЖК и SEO-блок «Люди часто спрашивают» (markedListV2).
 *
 * Вызывается в `resolveOffersData` на обоих путях — SSR и браузерный перезапрос, поэтому
 * в стор и в initialState попадают уже чистые строки, а компоненты рендерят их как есть.
 * Санитизация в рендере на ноде недопустима: DOMPurify поверх jsdom удерживает память
 * на каждый вызов и замедляется по мере роста heap (см. PERFORMANCE_TTFB.MD).
 */
export function sanitizeOffersData(offersData: IOffersDataState, sanitizeHtml: TSanitizeHtml): IOffersDataState {
  const markedListV2 = offersData.seo?.markedListV2;

  return {
    ...offersData,
    newbuildings: (offersData.newbuildings || []).map(newbuilding => sanitizeNewbuilding(newbuilding, sanitizeHtml)),
    similarNewbuildings: (offersData.similarNewbuildings || []).map(newbuilding =>
      sanitizeNewbuilding(newbuilding, sanitizeHtml),
    ),
    ...(markedListV2 && offersData.seo
      ? {
          seo: {
            ...offersData.seo,
            markedListV2: {
              ...markedListV2,
              items: (markedListV2.items || []).map(item => sanitizeMarkedListItem(item, sanitizeHtml)),
            },
          },
        }
      : {}),
  };
}
